import { useState } from "react";
import Seo from "@/components/base/Seo";
import AdminPageHeader from "../components/AdminPageHeader";
import SocialSharePreview from "@/components/feature/SocialSharePreview";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { adminSeoSettings } from "@/mocks/adminData";
import { clampText, deriveKeywords, scoreSeo } from "@/utils/seo";

const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none";
const labelClass = "mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-500";

export default function AdminSeo() {
  const { settings } = useSiteSettings();
  const [form, setForm] = useState({ ...adminSeoSettings });
  const [notice, setNotice] = useState("");

  const { score, checks } = scoreSeo({
    title: form.metaTitle,
    description: form.metaDescription,
    keywords: form.keywords,
    canonical: form.canonical,
  });

  const autoGenerate = () => {
    setForm((f) => ({
      ...f,
      metaTitle: clampText(f.metaTitle || "Mysore Yellow Pages — Local Business Directory", 60),
      metaDescription: clampText(f.metaDescription, 160),
      keywords: deriveKeywords(f.metaTitle || f.canonical, "/"),
    }));
    setNotice("SEO fields auto-generated and length-optimised.");
    setTimeout(() => setNotice(""), 2600);
  };

  const save = (e: React.FormEvent) => {
    e.preventDefault();
    setNotice("SEO settings saved.");
    setTimeout(() => setNotice(""), 2600);
  };

  return (
    <>
      <Seo title="SEO Settings" path="/admin/seo" />

      <AdminPageHeader
        icon="ri-seo-line"
        title="SEO settings"
        subtitle="Default metadata and indexing controls for the whole website."
        actions={
          notice ? (
            <span className="inline-flex items-center gap-2 rounded-md border border-accent-200 bg-accent-50 px-4 py-2 text-sm text-accent-900">
              <i className="ri-checkbox-circle-line text-accent-600"></i> {notice}
            </span>
          ) : undefined
        }
      />

      <form onSubmit={save} className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-5">
        <div className="rounded-lg border border-background-200 bg-background-50 p-5">
          <h2 className="font-heading text-base font-bold text-foreground-950">Default metadata</h2>
          <div className="mt-4 space-y-4">
            <div>
              <label htmlFor="metaTitle" className={labelClass}>Meta title</label>
              <input id="metaTitle" value={form.metaTitle} onChange={(e) => setForm({ ...form, metaTitle: e.target.value })} className={inputClass} />
              <p className="mt-1 text-xs text-foreground-400">{form.metaTitle.length} / 60 characters</p>
            </div>
            <div>
              <label htmlFor="metaDescription" className={labelClass}>Meta description</label>
              <textarea
                id="metaDescription"
                rows={3}
                value={form.metaDescription}
                onChange={(e) => setForm({ ...form, metaDescription: e.target.value })}
                className={inputClass}
              ></textarea>
              <p className="mt-1 text-xs text-foreground-400">{form.metaDescription.length} / 160 characters</p>
            </div>
            <div>
              <label htmlFor="keywords" className={labelClass}>Keywords</label>
              <input id="keywords" value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} className={inputClass} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="canonical" className={labelClass}>Canonical base URL</label>
                <input id="canonical" value={form.canonical} onChange={(e) => setForm({ ...form, canonical: e.target.value })} className={inputClass} />
              </div>
              <div>
                <label htmlFor="robots" className={labelClass}>Robots</label>
                <select id="robots" value={form.robots} onChange={(e) => setForm({ ...form, robots: e.target.value })} className={`${inputClass} cursor-pointer`}>
                  <option value="index, follow">index, follow</option>
                  <option value="noindex, follow">noindex, follow</option>
                  <option value="index, nofollow">index, nofollow</option>
                  <option value="noindex, nofollow">noindex, nofollow</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <aside className="space-y-4">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-sm font-bold text-foreground-950">SEO score</h2>
              <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ${score >= 80 ? "bg-accent-100 text-accent-900" : score >= 50 ? "bg-primary-100 text-primary-900" : "bg-secondary-100 text-secondary-900"}`}>
                {score}/100
              </span>
            </div>
            <ul className="mt-3 space-y-2">
              {checks.map((check) => (
                <li key={check.label} className="flex items-start gap-2 text-xs">
                  <i className={`${check.ok ? "ri-checkbox-circle-fill text-accent-600" : "ri-error-warning-line text-secondary-600"} mt-0.5`}></i>
                  <span className="text-foreground-700">
                    {check.label}
                    <span className="ml-1 text-foreground-400">· {check.hint}</span>
                  </span>
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={autoGenerate}
              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-800 hover:border-primary-400 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-magic-line"></i> Auto-generate fields
            </button>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-sm font-bold text-foreground-950">Indexing</h2>
            <label className="mt-4 flex items-center justify-between gap-3">
              <span className="text-sm text-foreground-700">Sitemap generation</span>
              <input
                type="checkbox"
                checked={form.sitemapEnabled}
                onChange={(e) => setForm({ ...form, sitemapEnabled: e.target.checked })}
                className="h-5 w-9 cursor-pointer accent-[oklch(var(--primary-500))]"
              />
            </label>
            <label className="mt-4 flex items-center justify-between gap-3">
              <span className="text-sm text-foreground-700">Google Search Console</span>
              <input
                type="checkbox"
                checked={form.gscVerified}
                onChange={(e) => setForm({ ...form, gscVerified: e.target.checked })}
                className="h-5 w-9 cursor-pointer accent-[oklch(var(--primary-500))]"
              />
            </label>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-sm font-bold text-foreground-950">Social share checker</h2>
            <p className="mt-1 text-xs text-foreground-500">
              How a link to this page looks when shared on Facebook, WhatsApp, X or LinkedIn — and whether the
              image is the recommended size.
            </p>
            <div className="mt-3">
              <SocialSharePreview
                imageUrl={settings.ogImageUrl}
                title={form.metaTitle}
                description={form.metaDescription}
                url={form.canonical}
              />
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-100/60 p-5">
            <h2 className="font-heading text-sm font-bold text-foreground-950">Search preview</h2>
            <div className="mt-3 rounded-md border border-background-200 bg-background-50 p-3">
              <p className="truncate text-xs text-accent-700">{form.canonical}</p>
              <p className="mt-1 line-clamp-2 text-sm font-semibold text-foreground-900">{form.metaTitle}</p>
              <p className="mt-1 line-clamp-3 text-xs text-foreground-600">{form.metaDescription}</p>
            </div>
          </div>

          <button
            type="submit"
            className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-bold text-foreground-950 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-save-line"></i> Save SEO settings
          </button>
        </aside>
      </form>
    </>
  );
}